import useSWR from 'swr';

import { useSession } from '../../../app/providers/sessionContext.js';
import { fetchCurrentContext, type CurrentContext } from '../data/identityRepository.js';

export const CURRENT_CONTEXT_KEY = 'identity/current-context';

export type CurrentContextState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; context: CurrentContext };

/**
 * `getCurrentContext()` from the Phase 0 plan: the base hook every screen builds on.
 * Creates the personal space on first login, then caches it for the session.
 */
export function useCurrentContext(): CurrentContextState {
  const session = useSession();
  const user = session.status === 'signedIn' ? session.session.user : null;

  const metadata: Record<string, unknown> = user?.user_metadata ?? {};
  const metadataName = metadata['display_name'];
  const fallbackName =
    (typeof metadataName === 'string' && metadataName.trim() !== '' ? metadataName : null) ??
    user?.email?.split('@')[0] ??
    'Tôi';

  // The error type is pinned so the message shown to the user is always the normalised
  // one from AppErrorException, never a raw Postgres string (skill 27).
  const { data, error, isLoading, mutate } = useSWR<CurrentContext, Error>(
    user ? CURRENT_CONTEXT_KEY : null,
    () => fetchCurrentContext(fallbackName),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được thông tin tài khoản.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || !data) {
    return { status: 'loading' };
  }

  return { status: 'ready', context: data };
}
